// src/app/services/accountant/index.tsx
import api, { publicAPI } from '..';
import { ITimezone } from '../constants';
import { IUserRole } from '../user';
import { IDepartment } from 'utils/departments'; // import the shared department interface

export interface IAccountantProfilesResponse {
  accountant_profile: {
    id: number;
    user_id: number;
    about: string;
    address: string;
    time_zone_id: string;
    zipcode: string;
    title: string;
    family_name: string;
    given_name: string;
    email: string;
    picture_url?: string;
    disabled: boolean;
    accountant_profile_work_experiences: [IAccountantWorkExperiences];
    accountant_profile_languages: [IAccountantLanguages];
    accountant_profile_skills: [Ioptions];
    accountant_profile_educations: [IAccountantEducation];
    accountant_license_and_certificates: [IAccountantLicenses];
    accountant_profile_specializations: [IAccountantSpecializations];
    schedule_platform_link?: string;
    time_zone?: ITimezone;
    roles: IUserRole[];
    billing_bundle_id: number;
    sub_accountants_count: number;
    sub_accountants_ids: number[];
    business_count: number;
    linkedin_id?: string;
    linkedin_picture?: string;
    linkedin_token_expires_at?: string;
    // Here the backend returns at least the department id,
    // which will be used with getDepartmentById() on the frontend.
    departments?: IDepartment[];
  };
}

export interface IAccountantWorkExperiences {
  id: number;
  company: string;
  ended_at: string;
  started_at: string;
  title: string;
}

export interface IAccountantLicenses {
  id: number;
  description: string;
  name: string;
  started_at: string;
  ended_at: string;
}

export interface IAccountantEducation {
  id: number;
  degree: string;
  grade: string;
  name: string;
  started_at: string;
  ended_at: string;
}

export interface Ioptions {
  id: number;
  name: string;
}

export interface IAccountantLanguages {
  id: number;
  language_name: Ioptions;
  language_proficiency_name: Ioptions;
}

export interface IAccountantSpecializations {
  id: number;
  accountant_profile_specialization_name: Ioptions;
}

export interface ILanguagesResponse {
  languages: Ioptions[];
  pagination: any;
}

export interface ISpecializationsResponse {
  specializations: Ioptions[];
  pagination: any;
}

export interface ILanguagesProficienciesResponse {
  language_proficiencies: Ioptions[];
}

export interface IAccountantWorkExperiencesResponse {
  accountant_profile_work_experiences: IAccountantWorkExperiences[];
}

export interface IAccountantEducationsResponse {
  accountant_profile_educations: IAccountantEducation[];
}

const AccountantPublicApi = publicAPI.injectEndpoints({
  endpoints: (builder) => ({
    signupAccountant: builder.mutation({
      query: ([data]) => ({
        url: `accountant_signup_requests`,
        method: 'POST',
        body: { accountant_signup_request: data },
      }),
    }),
  }),
});

export const { useSignupAccountantMutation } = AccountantPublicApi;

export const AccountantApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAccountantProfiles: builder.query<IAccountantProfilesResponse, number>({
      query: (id: number) => `accountant_profiles/${id}`,
      providesTags: () => [
        { type: 'Accountant', id: 'LIST' },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'Profile' },
        { type: 'Accountant', id: 'Profile' },
      ],
    }),
    updateAccountantProfiles: builder.mutation({
      query: ([data, id]) => ({
        url: `accountant_profiles/${id}`,
        method: 'PUT',
        body: { accountant_profile: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),

    // WorkExperiences
    getAccountantProfilesWorkExperiences: builder.query<
      IAccountantWorkExperiencesResponse,
      any
    >({
      query: () =>
        `accountant_profiles/accountant_profile_work_experiences?per_page=999&page=1`,
      providesTags: () => [{ type: 'Accountant', id: 'LIST' }],
    }),
    updateAccountantProfilesWorkExperiences: builder.mutation({
      query: ([data, id]) => ({
        url: `accountant_profiles/accountant_profile_work_experiences/${id}`,
        method: 'PUT',
        body: { work_experience: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    createAccountantProfilesWorkExperiences: builder.mutation({
      query: ([data]) => ({
        url: `accountant_profiles/accountant_profile_work_experiences`,
        method: 'POST',
        body: { work_experience: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    deleteAccountantProfilesWorkExperiences: builder.mutation({
      query: (id: number) => ({
        url: `accountant_profiles/accountant_profile_work_experiences/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),

    // Languages
    updateAccountantProfilesLanguages: builder.mutation({
      query: ([data, id]) => ({
        url: `accountant_profiles/accountant_profile_languages/${id}`,
        method: 'PUT',
        body: { accountant_profile_language: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    createAccountantProfilesLanguages: builder.mutation({
      query: ([data]) => ({
        url: `accountant_profiles/accountant_profile_languages`,
        method: 'POST',
        body: { accountant_profile_language: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    deleteAccountantProfilesLanguages: builder.mutation({
      query: (id: number) => ({
        url: `accountant_profiles/accountant_profile_languages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    getLanguages: builder.query<any, void>({
      query: () => 'languages?per_page=200&page=1',
      transformResponse: (res: ILanguagesResponse) => {
        return res.languages.map((l: Ioptions) => {
          return {
            value: l.id,
            label: l.name,
          };
        });
      },
    }),
    getLanguagesProficiencies: builder.query<any, void>({
      query: () => 'language_proficiencies',
      transformResponse: (res: ILanguagesProficienciesResponse) => {
        return res.language_proficiencies.map((l: Ioptions) => {
          return {
            value: l.id,
            label: l.name,
          };
        });
      },
    }),
    // Skills
    createAccountantProfilesSkills: builder.mutation({
      query: ([data]) => ({
        url: `accountant_profiles/accountant_profile_skills`,
        method: 'POST',
        body: { accountant_profile_skill: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    deleteAccountantProfilesSkills: builder.mutation({
      query: (id: number) => ({
        url: `accountant_profiles/accountant_profile_skills/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),

    // Education
    updateAccountantProfilesEducation: builder.mutation({
      query: ([data, id]) => ({
        url: `accountant_profiles/accountant_profile_educations/${id}`,
        method: 'PUT',
        body: { accountant_profile_education: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    createAccountantProfilesEducation: builder.mutation({
      query: ([data]) => ({
        url: `accountant_profiles/accountant_profile_educations`,
        method: 'POST',
        body: { accountant_profile_education: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    deleteAccountantProfilesEducation: builder.mutation({
      query: (id: number) => ({
        url: `accountant_profiles/accountant_profile_educations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    // Licenses
    updateAccountantProfilesLicenses: builder.mutation({
      query: ([data, id]) => ({
        url: `accountant_profiles/accountant_license_and_certificates/${id}`,
        method: 'PUT',
        body: { license_and_certificate: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    createAccountantProfilesLicenses: builder.mutation({
      query: ([data]) => ({
        url: `accountant_profiles/accountant_license_and_certificates`,
        method: 'POST',
        body: { license_and_certificate: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    deleteAccountantProfilesLicenses: builder.mutation({
      query: (id: number) => ({
        url: `accountant_profiles/accountant_license_and_certificates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    // Specializations
    getSpecializations: builder.query<{ value: string; label: string }[], void>(
      {
        query: () => 'specializations?per_page=200&page=1',
        transformResponse: (res: ISpecializationsResponse) => {
          return res.specializations.map((s: Ioptions) => {
            return {
              value: s.id.toString(),
              label: s.name,
            };
          });
        },
      },
    ),
    updateAccountantProfilesSpecializations: builder.mutation({
      query: ([data, id]) => ({
        url: `accountant_profiles/accountant_profile_specializations/${id}`,
        method: 'PUT',
        body: { accountant_profile_language: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    createAccountantProfilesSpecializations: builder.mutation({
      query: ([data]) => ({
        url: `accountant_profiles/accountant_profile_specializations`,
        method: 'POST',
        body: { accountant_profile_specialization: data },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    deleteAccountantProfilesSpecializations: builder.mutation({
      query: (id: number) => ({
        url: `accountant_profiles/accountant_profile_specializations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'LIST' }],
    }),
    // Activate/Deactivate accountant
    activateAccountant: builder.mutation<void, ['enable' | 'disable', number]>({
      query: ([activate, acccountantId]) => ({
        url: `/users/${acccountantId}/${activate}`,
        method: 'POST',
      }),
    }),
    // Assign Admin role
    assignAdmin: builder.mutation<void, number>({
      query: (acccountantId) => ({
        url: `/users/${acccountantId}/roles/assign`,
        body: {
          role: { name: 'ADMIN' },
        },
        method: 'PUT',
      }),
      invalidatesTags: () => [
        { type: 'Accountant', id: 'LIST' },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'Profile' },
      ],
    }),
    // Revoke Admin role
    revokeAdmin: builder.mutation<void, number>({
      query: (acccountantId) => ({
        url: `/users/${acccountantId}/roles/revoke`,
        body: {
          role: { name: 'ADMIN' },
        },
        method: 'PUT',
      }),
      invalidatesTags: () => [
        { type: 'Accountant', id: 'LIST' },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'Profile' },
      ],
    }),
    // Add new Accountant
    createNewAccountant: builder.mutation({
      query: ([data]) => ({
        url: `users/accountant/invite`,
        method: 'POST',
        body: { user: data },
      }),
      invalidatesTags: [
        { type: 'Accountant', id: 'LIST' },
        { type: 'Users', id: 'LIST' },
      ],
    }),

    // ===================== New Department Endpoints =====================
    assignDepartment: builder.mutation<
      any,
      { accountantId: number; departmentId: number }
    >({
      query: ({ accountantId, departmentId }) => ({
        url: `accountant_profiles/${accountantId}/assign_department`,
        method: 'POST',
        body: { department_id: departmentId },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'Profile' }],
    }),
    unassignDepartment: builder.mutation<
      any,
      { accountantId: number; departmentId: number }
    >({
      query: ({ accountantId, departmentId }) => ({
        url: `accountant_profiles/${accountantId}/unassign_department`,
        method: 'DELETE',
        body: { department_id: departmentId },
      }),
      invalidatesTags: [{ type: 'Accountant', id: 'Profile' }],
    }),
    getDepartments: builder.query<{ departments: IDepartment[] }, void>({
      query: () => 'departments',
    }),
    // =====================================================================
  }),
});

export const {
  useGetAccountantProfilesQuery,
  useLazyGetAccountantProfilesQuery,
  useUpdateAccountantProfilesMutation,
  useGetAccountantProfilesWorkExperiencesQuery,
  useDeleteAccountantProfilesWorkExperiencesMutation,
  useUpdateAccountantProfilesWorkExperiencesMutation,
  useCreateAccountantProfilesLanguagesMutation,
  useUpdateAccountantProfilesLanguagesMutation,
  useDeleteAccountantProfilesLanguagesMutation,
  useCreateAccountantProfilesWorkExperiencesMutation,
  useGetLanguagesQuery,
  useGetLanguagesProficienciesQuery,
  useCreateAccountantProfilesSkillsMutation,
  useDeleteAccountantProfilesSkillsMutation,
  useCreateAccountantProfilesEducationMutation,
  useDeleteAccountantProfilesEducationMutation,
  useUpdateAccountantProfilesEducationMutation,
  useCreateAccountantProfilesLicensesMutation,
  useDeleteAccountantProfilesLicensesMutation,
  useUpdateAccountantProfilesLicensesMutation,
  useGetSpecializationsQuery,
  useCreateAccountantProfilesSpecializationsMutation,
  useDeleteAccountantProfilesSpecializationsMutation,
  useUpdateAccountantProfilesSpecializationsMutation,
  useActivateAccountantMutation,
  useAssignAdminMutation,
  useRevokeAdminMutation,
  useCreateNewAccountantMutation,
  // New Department Hooks
  useAssignDepartmentMutation,
  useUnassignDepartmentMutation,
  useGetDepartmentsQuery,
} = AccountantApi;
